/**
 * Personality Selector Modal
 * Quick selector for choosing yanamas' personality before starting a conversation
 */

import React, { useState, useEffect } from 'react'
import { X, CheckCircle } from 'lucide-react'
import { Button } from '../ui/Button'
import { profileService, type Persona } from '../../services/profileService'
import { BRAND } from '../../config/brand'

interface PersonalitySelectorProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (emotionalApproach: string, conversationStyle: string) => void
  currentEmotionalApproach?: string
  currentConversationStyle?: string
}

export const PersonalitySelector: React.FC<PersonalitySelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentEmotionalApproach,
  currentConversationStyle
}) => {
  const [personas, setPersonas] = useState<Record<string, Persona>>({})
  const [selectedPersonaKey, setSelectedPersonaKey] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadOptions()
    }
  }, [isOpen])

  useEffect(() => {
    // Find which persona matches current settings
    if (currentEmotionalApproach && currentConversationStyle) {
      const matchingPersona = Object.entries(personas).find(([, persona]) =>
        persona.emotional_approach === currentEmotionalApproach &&
        persona.conversation_style === currentConversationStyle
      )
      if (matchingPersona) {
        setSelectedPersonaKey(matchingPersona[0])
      }
    }
  }, [currentEmotionalApproach, currentConversationStyle, personas])

  const loadOptions = async () => {
    try {
      setIsLoading(true)
      const personasData = await profileService.getPersonas()
      setPersonas(personasData)
    } catch (error) {
      console.error('Error loading personality options:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirm = () => {
    if (selectedPersonaKey && personas[selectedPersonaKey]) {
      const persona = personas[selectedPersonaKey]
      onSelect(persona.emotional_approach, persona.conversation_style)
    }
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-surface border-b border-soft px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-primary">
                ✨ Choose Your {BRAND.assistant}
              </h2>
              <p className="text-sm text-muted mt-1">
                Pick the personality that fits your needs right now
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-muted hover:text-secondary transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
            </div>
          ) : (
            <>
              {/* Personas Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {Object.entries(personas).map(([key, persona]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedPersonaKey(key)}
                    className={`text-left transition-all duration-200 rounded-xl border-2 p-4 ${
                      selectedPersonaKey === key
                        ? 'border-brand bg-brand-subtle shadow-lg scale-105'
                        : 'border-soft hover:border-soft hover:shadow-md'
                    }`}
                  >
                    {/* Emoji Face */}
                    <div className="text-4xl mb-2">{persona.emoji}</div>
                    
                    {/* Name and Selection Indicator */}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-base text-primary">
                          {persona.persona_name}
                        </h3>
                        <p className="text-xs text-secondary italic">
                          {persona.tagline}
                        </p>
                      </div>
                      {selectedPersonaKey === key && (
                        <CheckCircle className="h-5 w-5 text-brand flex-shrink-0" />
                      )}
                    </div>
                    
                    {/* Characteristics */}
                    <div className="space-y-1 mb-2">
                      {persona.characteristics.slice(0, 2).map((char, idx) => (
                        <p key={idx} className="text-xs text-secondary">
                          • {char}
                        </p>
                      ))}
                    </div>
                    
                    {/* Best For Tags */}
                    <div className="flex flex-wrap gap-1">
                      {persona.best_for.slice(0, 2).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-surface-alt text-secondary rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>

              {/* Selected Persona Preview */}
              {selectedPersonaKey && personas[selectedPersonaKey] && (
                <div className="bg-brand-subtle border-2 border-brand-subtle rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-5xl">{personas[selectedPersonaKey].emoji}</span>
                    <div>
                      <h4 className="font-bold text-lg text-primary">
                        {personas[selectedPersonaKey].persona_name}
                      </h4>
                      <p className="text-sm text-secondary">
                        {personas[selectedPersonaKey].tagline}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <p className="text-secondary">
                      <strong>Best for:</strong> {personas[selectedPersonaKey].best_for.join(', ')}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-surface border-t border-soft px-6 py-4 flex justify-end gap-3">
          <Button
            onClick={onClose}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !selectedPersonaKey}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Start Chat with This {BRAND.assistant}
          </Button>
        </div>
      </div>
    </div>
  )
}
